import { useQuery } from "@tanstack/react-query";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { apiGet } from "@/lib/api-client";
import type { WatchlistItem } from "@/sdk/types/data";

interface WatchlistResponse {
  items?: WatchlistItem[];
  // Backend degraded path: HTTP 200 with items=[] plus this explanation —
  // those zero items are NOT a genuinely empty watchlist.
  error?: string;
}

export function WatchlistPanel() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["watchlist"],
    queryFn: () => apiGet<WatchlistResponse>("/api/watchlist"),
    refetchInterval: 30_000,
  });

  const items = data?.items ?? [];

  if (isLoading) return <LoadingState text="Loading watchlist..." />;

  return (
    <Panel title="Watchlist">
      {isError ? (
        <QueryErrorState what="watchlist" onRetry={() => void refetch()} />
      ) : data?.error ? (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
          <p className="text-sm text-amber-300">Watchlist unavailable</p>
          <p className="mt-0.5 text-xs text-white/40">{data.error}</p>
        </div>
      ) : items.length === 0 ? (
        <p className="py-4 text-center text-sm text-white/40">Watchlist empty</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((item, index) => (
            <div key={`${item.market_id}-${index}`} className="flex items-center justify-between rounded border border-white/5 px-3 py-2">
              <div>
                <p className="max-w-[200px] truncate text-xs text-white/80">{item.question ?? item.market_id}</p>
                {item.tags && item.tags.length > 0 ? (
                  <div className="mt-0.5 flex gap-1">
                    {item.tags.map((tag) => (
                      <span key={tag} className="rounded bg-white/10 px-1 text-xs text-white/40">
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="text-right">
                {item.current_price != null ? <p className="text-sm font-semibold text-white/80">{Math.round(item.current_price * 100)}%</p> : null}
                {item.target_price != null ? <p className="text-xs text-white/30">target: {Math.round(item.target_price * 100)}%</p> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
